import { supabase } from '../supabase';
import { parseBoolean, isMaybeOpenToWork } from '../utils/boolean';

/**
 * Get all candidates for a user
 */
export const getCandidates = async (userId, filters = {}) => {
  let query = supabase
    .from('candidates')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  // Status filters are applied client-side in useCandidates to handle
  // is_open_to_work / invalid_link stored as boolean or text.

  if (filters.search) {
    query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,current_company.ilike.%${filters.search}%`);
  }

  if (filters.prioritize === true) {
    query = query.eq('prioritize', true);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
};

/**
 * Get single candidate by ID
 */
export const getCandidate = async (candidateId) => {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('id', candidateId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * Create new candidate
 */
export const createCandidate = async (userId, candidateData) => {
  const { data, error } = await supabase
    .from('candidates')
    .insert({
      user_id: userId,
      status: 'active',
      ...candidateData,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update candidate
 * @param {Object} options - { updateTimestamp: true } - when false, updated_at is not changed
 */
export const updateCandidate = async (candidateId, updates, options = {}) => {
  const { updateTimestamp = true } = options;
  const payload = { ...updates };
  if (updateTimestamp) {
    payload.updated_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('candidates')
    .update(payload)
    .eq('id', candidateId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete candidate
 */
export const deleteCandidate = async (candidateId) => {
  const { error } = await supabase
    .from('candidates')
    .delete()
    .eq('id', candidateId);

  if (error) throw error;
};

/**
 * Format date for PostgreSQL: 2025-11-26 11:32:50+00
 */
const toPgTimestamp = (date = new Date()) => {
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '+00');
};

/**
 * Bulk import candidates
 */
export const bulkImportCandidates = async (userId, candidatesArray) => {
  const now = toPgTimestamp();
  const candidates = candidatesArray.map(candidate => ({
    id: crypto.randomUUID(),
    user_id: userId,
    status: 'active',
    created_at: now,
    updated_at: now,
    ...candidate,
  }));

  const { data, error } = await supabase
    .from('candidates')
    .insert(candidates)
    .select();

  if (error) throw error;
  return data;
};

/**
 * Find which of the given LinkedIn URLs already exist in this user's candidates.
 * Returns a `Map<linkedin_url, id>`.
 */
const LINKEDIN_LOOKUP_BATCH_SIZE = 80;

export const findCandidatesByLinkedinUrls = async (userId, linkedinUrls) => {
  const result = new Map();
  const urls = [...new Set((linkedinUrls || []).filter(Boolean))];
  if (urls.length === 0) return result;

  for (let i = 0; i < urls.length; i += LINKEDIN_LOOKUP_BATCH_SIZE) {
    const batch = urls.slice(i, i + LINKEDIN_LOOKUP_BATCH_SIZE);
    const { data, error } = await supabase
      .from('candidates')
      .select('id, linkedin_url')
      .eq('user_id', userId)
      .in('linkedin_url', batch);

    if (error) throw error;
    for (const row of data || []) {
      result.set(row.linkedin_url, row.id);
    }
  }

  return result;
};

const isUniqueViolation = (error) =>
  error?.code === '23505' || String(error?.message || '').includes('unique_user_linkedin_url');

const insertCandidatesResilient = async (rows) => {
  const inserted = [];
  const skippedDuplicates = [];

  if (rows.length === 0) {
    return { inserted, skippedDuplicates };
  }

  const { data, error } = await supabase.from('candidates').insert(rows).select();

  if (!error) {
    return { inserted: data || [], skippedDuplicates };
  }

  if (!isUniqueViolation(error)) {
    throw error;
  }

  for (const row of rows) {
    const { data: one, error: oneError } = await supabase
      .from('candidates')
      .insert(row)
      .select()
      .single();

    if (oneError) {
      if (isUniqueViolation(oneError)) {
        skippedDuplicates.push({
          linkedin_url: row.linkedin_url,
          reason: 'duplicate_linkedin_url',
        });
        continue;
      }
      throw oneError;
    }
    inserted.push(one);
  }

  return { inserted, skippedDuplicates };
};

/**
 * Insert new candidates and update existing ones (matched by linkedin_url).
 *
 * Updates are partial: only fields present in `candidatesArray` are written,
 * so an empty Email column in the CSV will NOT clear an existing email in DB.
 *
 * Duplicate LinkedIn URLs within the same import batch are all skipped.
 *
 * Returns { inserted, updated, skippedDuplicates }.
 */
export const upsertCandidatesByLinkedinUrl = async (userId, candidatesArray) => {
  if (!candidatesArray || candidatesArray.length === 0) {
    return { inserted: [], updated: [], skippedDuplicates: [] };
  }

  const urlCounts = new Map();
  for (const candidate of candidatesArray) {
    const url = candidate.linkedin_url;
    if (!url) continue;
    urlCounts.set(url, (urlCounts.get(url) || 0) + 1);
  }
  const duplicateUrls = new Set(
    [...urlCounts.entries()].filter(([, count]) => count > 1).map(([url]) => url),
  );

  const existing = await findCandidatesByLinkedinUrls(
    userId,
    candidatesArray.map((c) => c.linkedin_url),
  );

  const now = toPgTimestamp();
  const toInsert = [];
  const toUpdate = [];
  const skippedDuplicates = [];
  const reportedDuplicateUrls = new Set();

  for (const candidate of candidatesArray) {
    const url = candidate.linkedin_url;
    if (!url) continue;

    if (duplicateUrls.has(url)) {
      if (!reportedDuplicateUrls.has(url)) {
        skippedDuplicates.push({ linkedin_url: url, reason: 'duplicate_in_batch' });
        reportedDuplicateUrls.add(url);
      }
      continue;
    }

    const existingId = existing.get(url);
    if (existingId) {
      toUpdate.push({ id: existingId, payload: { ...candidate, updated_at: now } });
    } else {
      toInsert.push({
        id: crypto.randomUUID(),
        user_id: userId,
        status: 'active',
        created_at: now,
        updated_at: now,
        ...candidate,
      });
    }
  }

  let inserted = [];
  if (toInsert.length > 0) {
    const insertResult = await insertCandidatesResilient(toInsert);
    inserted = insertResult.inserted;
    skippedDuplicates.push(...insertResult.skippedDuplicates);
  }

  const updated = [];
  for (const { id, payload } of toUpdate) {
    const { data, error } = await supabase
      .from('candidates')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    updated.push(data);
  }

  return { inserted, updated, skippedDuplicates };
};

/**
 * Toggle candidate blacklist
 */
export const toggleBlacklist = async (candidateId, blacklisted) => {
  return updateCandidate(candidateId, { blacklist: blacklisted });
};

/**
 * Update next call date
 */
export const updateNextCallDate = async (candidateId, date) => {
  return updateCandidate(candidateId, { next_call_date: date });
};

/**
 * Get count of prioritized candidates for a user (max 5 allowed)
 */
export const getPrioritizedCandidatesCount = async (userId) => {
  const { count, error } = await supabase
    .from('candidates')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('prioritize', true);

  if (error) throw error;
  return count || 0;
};

/**
 * Candidates that can be uploaded back to Zoho (have zoho_id and scraped_time).
 */
export const getZohoUploadCandidatesCount = async (userId) => {
  const { count, error } = await supabase
    .from('candidates')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .not('zoho_id', 'is', null)
    .neq('zoho_id', '')
    .not('scraped_time', 'is', null);

  if (error) throw error;
  return count || 0;
};

/**
 * Resolve Zoho Recruit candidate ids to HirePortal candidates for the given user.
 * Returns a Map keyed by zoho_id string → { id, fullName, zohoId }.
 */
export const getCandidatesByZohoIds = async (userId, zohoIds = []) => {
  const ids = [...new Set(
    (zohoIds || [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  )];

  const byZohoId = new Map();
  if (!userId || ids.length === 0) return byZohoId;

  const { data, error } = await supabase
    .from('candidates')
    .select('id, full_name, zoho_id')
    .eq('user_id', userId)
    .in('zoho_id', ids);

  if (error) throw error;

  for (const row of data || []) {
    const zohoId = String(row.zoho_id ?? '').trim();
    if (!zohoId) continue;
    byZohoId.set(zohoId, {
      id: row.id,
      fullName: row.full_name || 'Unnamed candidate',
      zohoId,
    });
  }

  return byZohoId;
};

/**
 * Get total candidates count for a user (lightweight head query).
 */
export const getCandidatesCount = async (userId) => {
  const { count, error } = await supabase
    .from('candidates')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  if (error) throw error;
  return count || 0;
};

/**
 * Get candidates statistics for dashboard
 */
export const getCandidatesStats = async (userId) => {
  const { data: allCandidates, error } = await supabase
    .from('candidates')
    .select('is_open_to_work')
    .eq('user_id', userId);

  if (error) throw error;

  const stats = {
    total: allCandidates.length,
    activeSeekers: allCandidates.filter((c) => parseBoolean(c.is_open_to_work)).length,
    potentialSeekers: allCandidates.filter((c) => isMaybeOpenToWork(c.is_open_to_work)).length,
    stable: allCandidates.filter((c) => !parseBoolean(c.is_open_to_work)).length,
  };

  return stats;
};

/**
 * Get recent candidates
 */
export const getRecentCandidates = async (userId, limit = 10) => {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('user_id', userId)
    .order('scraped_time', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

/**
 * Get count of candidates updated in the last 7 days
 */
export const getCandidatesUpdatedLast7Days = async (userId) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { count, error } = await supabase
    .from('candidates')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('updated_at', sevenDaysAgo.toISOString());

  if (error) throw error;
  return count || 0;
};
